import React, { useState, useEffect, FormEvent, useLayoutEffect } from "react";
import {
  VStack,
  Box,
  Modal,
  FormControl,
  Input,
  HStack,
  ModalOverlay,
  ModalHeader,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Text,
  Button,
  useDisclosure,
  Progress,
} from "@chakra-ui/react";
import { uploadFile } from "../../api";
import {
  activateFreeTrial,
  cancelFreeTrialVerification,
  createSal125Order,
  fetchAuthUserMe,
  fetchSal125Quotations,
  repurchaseSal125Order,
  verifyFreeTrialCard,
} from "src/api/subscription";
import { FormattedMessage } from "react-intl";
import DarkHeader from "src/layout/DarkHeader";
import useLocale from "src/providers/useLocale";
import { LOGIN_ROUTE, SUBSCRIPTION_ROUTE } from "src/routePaths";
import { PaymentIntent } from "src/types";
import { useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import GoSellPayment from "src/views/Payment/GoSellPayment";
import { Link, Redirect, useLocation } from "react-router-dom";
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import { useSelector, useDispatch } from "src/store";
import { update } from "src/store/auth";
import { validateToken } from "src/validateToken";
import SubscriptionCard from "src/components/SubscriptionCard/SubscriptionCard";
import { useCheckSubscription } from "src/free-trial/useCheckSubscriptions";
import { useFreeTrial } from "src/free-trial/helpers";

type Quotation = {
  id?: number;
  duration?: number;
  price?: number;
  currency?: "USD";
  region?: string;
  selected?: string;
  isFreeTrial?: boolean;
};

const Subscription = () => {
  //map for currency symbols
  // to indicate heigh prices (245 or 375)
  const [heighPrices, setHeighPrices] = useState(false);

  //function for formatting messages
  const { formatMessage } = useIntl();

  //current locale
  const [locale] = useLocale();

  //disclosures for modal visibility
  const disclosureProps = useDisclosure();
  
  // Gateway selection modal
  const gatewayModalProps = useDisclosure();
  const [selectedGateway, setSelectedGateway] = useState<'tap' | 'myfatoorah' | null>(null);
  const [pendingQuotation, setPendingQuotation] = useState<Quotation | null>(null);
  const [pendingFreeTrial, setPendingFreeTrial] = useState(false);

  //subscribtion data
  const [subscriptionData, setSubscriptionData] = useState<{
    quotationId?: number;
    paymentMethod?: string;
    proveUrl?: string;
    nationalityProofUrl?: string;
    gateway?: 'tap' | 'myfatoorah';
  }>();

  //message to display after order creation attempt
  const [message, setMessage] = useState({ content: "", color: "" });

  //success status
  const [success, setSuccess] = useState<boolean>(false);

  //toast for errors while fetching gateway configurations
  const toast = useToast();

  //uploading files progress
  const [progress, setProgress] = useState(0);

  //processing the order
  const [processing, setProcessing] = useState(false);

  //configurations for payment gateway
  const [gatewayConfig, setGatewayConfig] = useState<PaymentIntent>();

  //current name for file upload proveUrl | nationalityProofUrl
  const [fileName, setFileName] = useState<string>();

  const { isTrialUser: isFreeTrial } = useFreeTrial(0);

  //current user
  const user = useSelector((state) => state.auth.user);

  //dispatch for updating user state
  const dispatch = useDispatch();

  //method for opening modal
  const openModal = (id: number, method: string) => {
    if (!method) return;
    setSubscriptionData({ quotationId: id, paymentMethod: method });
    disclosureProps.onOpen();
  };

  //quotations for subscriptions
  const [quotations, setQuotations] = useState<Quotation[]>([]);

  // is file uploaded (used to enable submit button)

  const [isfileUploaded, setIsFileUploaded] = useState<boolean>(false);

  const { state } = useLocation();
  const renew = state?.renew;

  //method for fetching quotations
  const fetchQuotations = async () => {
    const response = await fetchSal125Quotations(locale);
    if (response && Array.isArray(response)) {
      setQuotations(response);
    }
  };

  //function to fetch gate way configurations
  // const fetchGatewayConfig = async () => {
  //   const response: PaymentIntent = await get(
  //     "/payment/info",
  //     undefined,
  //     user?.accessToken
  //   );
  //   if (response?.gateway) {
  //     setGatewayConfig(response);
  //   } else {
  //     toast({
  //       title: formatMessage({ id: "wentWrong" }),
  //       status: "error",
  //       duration: 9000,
  //       isClosable: true,
  //     });
  //   }
  // };

  const [
    changeQuotationSectionVisibility,
    setChangeQuotationSectionVisibility,
  ] = useState<boolean>(false);

  const toggleChangeQuotations = () => {
    setFileName("proveUrl");
    setProcessing(false); // Reset processing state
    setPendingQuotation(null); // Clear any pending quotation
    setSelectedGateway(null); // Clear selected gateway
    setMessage({ content: "", color: "" }); // Clear any error messages
    setChangeQuotationSectionVisibility((currentState: boolean) => {
      return !currentState;
    });
  };
  //available payment methods

  //method for selecting payment method for quotation
  const selectMethod = (qId: number, name: string) => {
    const updatedQ = [...quotations];
    updatedQ.find((q) => q.id === qId)!.selected = name;
    setQuotations(updatedQ);
  };

  //method for handling media change
  const handleMedia = (e: FormEvent) => {
    setIsFileUploaded(false);
    if (progress) return;
    const target = e.target as HTMLInputElement;
    const file = target.files![0];
    const selectedQuotation = quotations.find((q) => q.selected);
    const successCb = (url: string) => {
      if (fileName) {
        setSubscriptionData({ ...subscriptionData, [fileName]: url });
      }
      //If that was the proof nationality step, go the the next step
      if (
        fileName === "nationalityProofUrl" ||
        selectedQuotation?.price === 245 ||
        selectedQuotation?.price === 375
      )
        setFileName("proveUrl");
    };
    const failureCb = () => {
      toast({
        title: formatMessage({ id: "upload.fail" }),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    };
    if (file) {
      uploadFile(file, setProgress, successCb, failureCb, user?.accessToken);
      setIsFileUploaded(true);
    }
  };

  //save the subscribtion
  const subscribe = () => {
    // if ((!subscribtionData?.proveUrl && subscribtionData?.paymentMethod !== 'card') || processing || (!heighPrices)) return;
    setProcessing(true);

    // For card payments: Open window synchronously BEFORE async API call
    // This prevents mobile popup blockers from blocking the window
    let paymentWindow: Window | null = null;
    if (subscriptionData?.paymentMethod === 'card') {
      paymentWindow = window.open('', '_blank');
      
      // Write loading page to the window
      if (paymentWindow && !paymentWindow.closed) {
        const loadingHTML = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Loading Payment...</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body { 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                height: 100vh; 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              }
              .loader { 
                text-align: center; 
                color: white;
              }
              .spinner {
                border: 4px solid rgba(255, 255, 255, 0.3);
                border-top: 4px solid white;
                border-radius: 50%;
                width: 50px;
                height: 50px;
                animation: spin 1s linear infinite;
                margin: 0 auto 24px;
              }
              .text {
                font-size: 18px;
                font-weight: 500;
                letter-spacing: 0.5px;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p class="text">Opening payment page...</p>
            </div>
          </body>
          </html>
        `;
        paymentWindow.document.write(loadingHTML);
        paymentWindow.document.close();
      }
    }

    createSal125Order(subscriptionData, locale)
      .then((res: any) => {
        setProcessing(false);
        if (res && res.id) {
          if (res.url) {
            //if url is in response, redirect them to it since it is the payment API url
            disclosureProps.onClose();
            
            // Redirect the already-opened window to payment gateway
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.location.href = res.url;
            } else {
              // Fallback: if window was blocked or closed, open new one
              window.open(res.url, "_blank");
            }
          } else {
            // Close payment window if no URL returned (e.g., bank transfer)
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.close();
            }
          }
          // setMessage({content: "orderSuccess", color: 'green'});
          setSuccess(true);
          setChangeQuotationSectionVisibility(false);
        } else {
          // Close payment window on error
          if (paymentWindow && !paymentWindow.closed) {
            paymentWindow.close();
          }
          setMessage({ content: "wentWrong", color: "crimson" });
        }
      })
      .catch((err) => {
        setProcessing(false);
        // Close payment window on error
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        setMessage({ content: err.message || "wentWrong", color: "crimson" });
      });
  };

  //save the subscribtion (heigh price case)
  const subscribeHeighPrice = (subData: any) => {
    setSubscriptionData({ ...subData });
    if (processing) return;
    setProcessing(true);

    // For card payments: Open window synchronously BEFORE async API call
    // This prevents mobile popup blockers from blocking the window
    let paymentWindow: Window | null = null;
    if (subData.paymentMethod === 'card') {
      paymentWindow = window.open('', '_blank');
      
      // Write loading page to the window
      if (paymentWindow && !paymentWindow.closed) {
        const loadingHTML = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Loading Payment...</title>
            <style>
              * {
                margin: 0;
                padding: 0;
                box-sizing: border-box;
              }
              body { 
                display: flex; 
                justify-content: center; 
                align-items: center; 
                height: 100vh; 
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              }
              .loader { 
                text-align: center; 
                color: white;
              }
              .spinner {
                border: 4px solid rgba(255, 255, 255, 0.3);
                border-top: 4px solid white;
                border-radius: 50%;
                width: 50px;
                height: 50px;
                animation: spin 1s linear infinite;
                margin: 0 auto 24px;
              }
              .text {
                font-size: 18px;
                font-weight: 500;
                letter-spacing: 0.5px;
              }
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            </style>
          </head>
          <body>
            <div class="loader">
              <div class="spinner"></div>
              <p class="text">Opening payment page...</p>
            </div>
          </body>
          </html>
        `;
        paymentWindow.document.write(loadingHTML);
        paymentWindow.document.close();
      }
    }

    createSal125Order(subData, locale)
      .then((res: any) => {
        setProcessing(false);
        if (res && res.id) {
          if (res.url) {
            //if url is in response, redirect them to it since it is the payment API url
            
            // Redirect the already-opened window to payment gateway
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.location.href = res.url;
            } else {
              // Fallback: if window was blocked or closed, open new one
              window.open(res.url, "_blank");
            }
          } else {
            // Close payment window if no URL returned (e.g., bank transfer)
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.close();
            }
          }
          // setMessage({content: "orderSuccess", color: 'green'});
          setSuccess(true);
          setChangeQuotationSectionVisibility(false);
        } else {
          // Close payment window on error
          if (paymentWindow && !paymentWindow.closed) {
            paymentWindow.close();
          }
          setMessage({ content: "wentWrong", color: "crimson" });
        }
      })
      .catch((err) => {
        setProcessing(false);
        // Close payment window on error
        if (paymentWindow && !paymentWindow.closed) {
          paymentWindow.close();
        }
        setMessage({ content: err.message || "wentWrong", color: "crimson" });
      });
  };

  //function to handle modal opening
  const handleOpening = (quotation: Quotation) => {
    // If card payment, show gateway selection modal first
    if (quotation.selected === 'card') {
      setPendingQuotation(quotation);
      gatewayModalProps.onOpen();
    } else {
      // For other payment methods, open the regular modal
      openModal(quotation.id!, quotation.selected!);
    }
  };

  const startFreeTrialVerification = (gateway: 'tap' | 'myfatoorah') => {
    setProcessing(true);
    verifyFreeTrialCard(gateway, locale)
      .then((res: {
        redirectUrl?: string;
        authorizationId?: string;
        gateway?: 'tap' | 'myfatoorah';
      }) => {
        setProcessing(false);
        if (res.redirectUrl && res.authorizationId) {
          localStorage.setItem('freeTrialAuthorizationId', res.authorizationId);
          localStorage.setItem('freeTrialGateway', res.gateway ?? gateway);
          window.location.href = res.redirectUrl;
        } else {
          toast({
            title: formatMessage({ id: 'wentWrong' }),
            status: 'error',
            isClosable: true,
          });
        }
      })
      .catch((err) => {
        setProcessing(false);
        toast({
          title: err.message || formatMessage({ id: 'wentWrong' }),
          status: 'error',
          isClosable: true,
        });
      });
  };

  const handleFreeTrialOpening = () => {
    setPendingFreeTrial(true);
    gatewayModalProps.onOpen();
  };

  // Handle gateway selection
  const handleGatewaySelection = (gateway: 'tap' | 'myfatoorah') => {
    setSelectedGateway(gateway);

    if (pendingFreeTrial) {
      gatewayModalProps.onClose();
      setPendingFreeTrial(false);
      startFreeTrialVerification(gateway);
      return;
    }

    if (pendingQuotation) {
      const quotation = pendingQuotation;
      setSubscriptionData({
        quotationId: quotation.id,
        paymentMethod: 'card',
        gateway: gateway,
      });
      gatewayModalProps.onClose();
      
      // Proceed with subscription
      const isValidPrice =
        quotation.price === 245 ||
        quotation.price === 375 ||
        quotation.price === 0;

      if (isValidPrice) {
        setHeighPrices(true);
        let subData = {
          quotationId: quotation.id,
          paymentMethod: 'card',
          gateway: gateway,
        };
        subscribeHeighPrice(subData);
        setChangeQuotationSectionVisibility(false);
      } else {
        // For regular prices, create order directly with gateway
        setProcessing(true);

        // For card payments: Open window synchronously BEFORE async API call
        // This prevents mobile popup blockers from blocking the window
        let paymentWindow: Window | null = window.open('', '_blank');
        
        // Write loading page to the window
        if (paymentWindow && !paymentWindow.closed) {
          const loadingHTML = `
            <!DOCTYPE html>
            <html>
            <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Loading Payment...</title>
              <style>
                * {
                  margin: 0;
                  padding: 0;
                  box-sizing: border-box;
                }
                body { 
                  display: flex; 
                  justify-content: center; 
                  align-items: center; 
                  height: 100vh; 
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }
                .loader { 
                  text-align: center; 
                  color: white;
                }
                .spinner {
                  border: 4px solid rgba(255, 255, 255, 0.3);
                  border-top: 4px solid white;
                  border-radius: 50%;
                  width: 50px;
                  height: 50px;
                  animation: spin 1s linear infinite;
                  margin: 0 auto 24px;
                }
                .text {
                  font-size: 18px;
                  font-weight: 500;
                  letter-spacing: 0.5px;
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              </style>
            </head>
            <body>
              <div class="loader">
                <div class="spinner"></div>
                <p class="text">Opening payment page...</p>
              </div>
            </body>
            </html>
          `;
          paymentWindow.document.write(loadingHTML);
          paymentWindow.document.close();
        }

        createSal125Order({
          quotationId: quotation.id,
          paymentMethod: 'card',
          gateway: gateway,
        }, locale)
          .then((res: any) => {
            setProcessing(false);
            if (res && res.id) {
              if (res.url) {
                // Redirect the already-opened window to payment gateway
                if (paymentWindow && !paymentWindow.closed) {
                  paymentWindow.location.href = res.url;
                } else {
                  // Fallback: if window was blocked or closed, open new one
                  window.open(res.url, "_blank");
                }
              } else {
                // Close payment window if no URL returned
                if (paymentWindow && !paymentWindow.closed) {
                  paymentWindow.close();
                }
              }
              setSuccess(true);
              setChangeQuotationSectionVisibility(false);
            } else {
              // Close payment window on error
              if (paymentWindow && !paymentWindow.closed) {
                paymentWindow.close();
              }
              setMessage({ content: "wentWrong", color: "crimson" });
            }
          })
          .catch((err) => {
            setProcessing(false);
            // Close payment window on error
            if (paymentWindow && !paymentWindow.closed) {
              paymentWindow.close();
            }
            setMessage({ content: err.message || "wentWrong", color: "crimson" });
          });
      }
      setPendingQuotation(null);
    }
  };

  // Handle gateway modal close - cleanup state
  const handleGatewayModalClose = () => {
    setPendingQuotation(null);
    setPendingFreeTrial(false);
    setSelectedGateway(null);
    setProcessing(false);
    gatewayModalProps.onClose();
  };

  // if subscribtion failed
  const [repurchase, setRepurchase] = useState<any>();
  useLayoutEffect(() => {
    validateToken();
  }, [validateToken]);

  // Refresh user data on mount and clean up abandoned verifications
  useEffect(() => {
    const refreshUserData = async () => {
      if (!user?.accessToken) return;

      try {
        // Fetch fresh user data from the backend
        const freshUserData = await fetchAuthUserMe(locale);
        
        if (freshUserData?.id) {
          // Update Redux store with fresh user data
          dispatch(update({ user: { ...freshUserData, accessToken: user.accessToken } }));
        }
      } catch (error) {
        console.error("Failed to refresh user data:", error);
        // Don't show error toast, fail silently
      }
    };

    // Clean up abandoned free trial verifications
    const cleanupAbandonedVerification = () => {
      const authorizationId = localStorage.getItem("freeTrialAuthorizationId");
      
      // If we have an authorizationId but we're on the subscription page (not the callback page),
      // it means the user abandoned the verification process
      if (authorizationId && !window.location.pathname.includes('/free-trial/callback')) {
        // Remove the stale authorizationId
        localStorage.removeItem("freeTrialAuthorizationId");
        
        // Optionally notify backend to clean up verification status
        if (user?.accessToken) {
          cancelFreeTrialVerification(authorizationId, locale).catch(() => {
            // Fail silently - cleanup is best effort
          });
        }
      }
    };

    // Execute both operations
    cleanupAbandonedVerification();
    refreshUserData();
  }, []); // Run only once on mount

  useEffect(() => {
    validateToken();
    fetchQuotations();
    disclosureProps.onClose();
    // if subscribtion failed
    repurchaseSal125Order(subscriptionData, locale)
      .then((res) => {
        setRepurchase(res);
      })
      .catch((err) => {
        setRepurchase(null);
      });
    // fetchGatewayConfig();
  }, [locale, processing]);

  if (!user) return <Redirect to={LOGIN_ROUTE} />;

  if (
    user.subscriptionStatus === "approved" &&
    !renew &&
    !isFreeTrial
  )
    return <Redirect to="/portal" />;

  if (user?.subscriptionStatus === "pending" || success)
    return (
      <>
        <DarkHeader />
        <Box padding="50px 20px 50px 20px">
          <Text color="green.500" textAlign="center">
            <FormattedMessage id="offerPending" />
            <br />
            <a href="https://t.me/SmartsWay125">https://t.me/SmartsWay125</a>
            {repurchase && repurchase?.order?.paymentMethod === "card" ? (
              <>
                <p>
                  <a
                    style={{
                      borderRadius: "5px",
                      margin: " 30px auto",
                      textAlign: "center",
                      display: "block",
                      width: "200px",
                      padding: "5px 10px",
                      color: "white",
                      background: "#1f3f93",
                    }}
                    href={repurchase.payment_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FormattedMessage id="completeProcess" />
                  </a>
                </p>
                <Link
                  to={{ pathname: SUBSCRIPTION_ROUTE, state: { renew } }}
                  onClick={() => {
                    toggleChangeQuotations();
                  }}
                  style={{
                    borderRadius: "5px",
                    margin: " 10px auto",
                    padding: "5px 10px",
                    textAlign: "center",
                    display: "block",
                    width: "200px",
                    color: "white",
                    background: "#1f3f93",
                  }}
                >
                  <FormattedMessage id="changePaymentMethod" />
                  {changeQuotationSectionVisibility ? (
                    <ChevronUpIcon />
                  ) : (
                    <ChevronDownIcon />
                  )}
                </Link>
              </>
            ) : (
              <Link
                to={{ pathname: SUBSCRIPTION_ROUTE, state: { renew } }}
                onClick={() => {
                  toggleChangeQuotations();
                }}
                style={{
                  borderRadius: "5px",
                  margin: " 10px auto",
                  padding: "5px 10px",
                  textAlign: "center",
                  display: "block",
                  width: "200px",
                  color: "white",
                  background: "#1f3f93",
                }}
              >
                <FormattedMessage id="changePaymentMethod" />
                {changeQuotationSectionVisibility ? (
                  <ChevronUpIcon />
                ) : (
                  <ChevronDownIcon />
                )}
              </Link>
            )}
          </Text>
        </Box>
        {changeQuotationSectionVisibility && (
          // @ts-igonore
          <VStack display="flex" flexDir="column" alignItems="center">
            <HStack
              display="flex"
              flexDir={{ base: "column", lg: "row" }}
              alignItems={{ base: "center" }}
            >
              {quotations.map((quotation) => {
                return (
                  <SubscriptionCard
                    key={quotation.id}
                    quotation={quotation}
                    selectMethod={selectMethod}
                    setHeighPrices={setHeighPrices}
                    setFileName={setFileName}
                    setSubscriptionData={setSubscriptionData}
                    subscribeHeighPrice={subscribeHeighPrice}
                    setChangeQuotationSectionVisibility={
                      setChangeQuotationSectionVisibility
                    }
                    handleOpening={handleOpening}
                    onFreeTrialSubscribe={handleFreeTrialOpening}
                    processing={processing}
                    isFreeTrial={quotation.price === 0}
                    selectPaymentMethods={quotation.price === 0 ? ["card"] : []}
                  />
                );
              })}
              {!user.subscriptions.some((sub) => sub.status === "active" && new Date(sub.endDate) > new Date()) && (
                <SubscriptionCard
                  quotation={{
                    duration: 7,
                    price: 0,
                  }}
                  selectMethod={selectMethod}
                  setHeighPrices={setHeighPrices}
                  setFileName={setFileName}
                  setSubscriptionData={setSubscriptionData}
                  subscribeHeighPrice={subscribeHeighPrice}
                  setChangeQuotationSectionVisibility={
                    setChangeQuotationSectionVisibility
                  }
                  handleOpening={handleOpening}
                  onFreeTrialSubscribe={handleFreeTrialOpening}
                  processing={processing}
                  isFreeTrial={true}
                  selectPaymentMethods={[]}
                />
              )}
            </HStack>
            <Modal {...disclosureProps} onClose={disclosureProps.onClose}>
              <ModalOverlay />
              <ModalContent
                width={{ base: "300px", sm: "400px", md: "500px" }}
                minWidth={{ lg: "600px" }}
                marginTop="150px"
              >
                <ModalHeader>
                  <ModalCloseButton />
                </ModalHeader>
                <ModalBody>
                  <FormControl as="form">
                    {message.content && (
                      <Box margin="30px 0">
                        <Text color={message.color}>
                          <FormattedMessage id={message.content || ""} />
                        </Text>
                      </Box>
                    )}
                    {processing && (
                      <Box margin="30px 0">
                        <Text color="grey">
                          <FormattedMessage id="wait" />
                        </Text>
                      </Box>
                    )}
                    {fileName === "proveUrl" ? (
                      <>
                        <Text color="grey" fontSize={18} fontWeight="bold">
                          <FormattedMessage id={fileName || ""} />
                        </Text>
                        <Box margin="30px 0">
                          <Input
                            type="file"
                            required
                            name={fileName}
                            onChange={handleMedia}
                            key={fileName}
                          />
                          {progress > 0 && <Progress value={progress} />}
                        </Box>
                      </>
                    ) : null}
                    {
                      // fileName === 'proveUrl' &&
                      <Box textAlign="center" margin="30px 0">
                        <Button
                          backgroundColor="brand.900"
                          color="white"
                          height="50px"
                          _hover={{ filter: "brightness(120%)" }}
                          onClick={() => {
                            subscribe();
                            disclosureProps.onClose();
                          }}
                          disabled={processing || !isfileUploaded}
                        >
                          <FormattedMessage id="subscribe" />
                        </Button>
                      </Box>
                    }
                  </FormControl>
                </ModalBody>
              </ModalContent>
            </Modal>
            
            {/* Gateway Selection Modal - needed for card payments in change payment flow */}
            <Modal {...gatewayModalProps} onClose={handleGatewayModalClose}>
              <ModalOverlay />
              <ModalContent
                width={{ base: "300px", sm: "400px", md: "500px" }}
                minWidth={{ lg: "500px" }}
                marginTop="150px"
              >
                <ModalHeader>
                  <FormattedMessage id="payment.gateway.select" defaultMessage="Select Payment Gateway" />
                  <ModalCloseButton />
                </ModalHeader>
                <ModalBody px={6}>
                  <VStack spacing={4} padding="20px 0">
                    <Text fontSize="16px" color="gray.600" textAlign="center">
                      <FormattedMessage 
                        id="payment.gateway.select.description" 
                        defaultMessage="Choose your preferred payment provider" 
                      />
                    </Text>
                    <HStack
                      spacing={4}
                      justifyContent="center"
                      alignItems="center"
                    >
                      <Button
                        px={8}
                        backgroundColor="brand.900"
                        color="white"
                        height="60px"
                        _hover={{ filter: "brightness(120%)" }}
                        onClick={() => handleGatewaySelection('tap')}
                      >
                        <FormattedMessage id="gateway.tap" defaultMessage="Tap" />
                      </Button>
                      <Button
                        px={8}
                        backgroundColor="#1a73e8"
                        color="white"
                        height="60px"
                        _hover={{ filter: "brightness(120%)" }}
                        onClick={() => handleGatewaySelection('myfatoorah')}
                      >
                        <FormattedMessage id="gateway.myfatoorah" defaultMessage="MyFatoorah" />
                      </Button>
                    </HStack>
                  </VStack>
                </ModalBody>
              </ModalContent>
            </Modal>
          </VStack>
        )}
      </>
    );

  return (
    <>
      <DarkHeader />
      {gatewayConfig && gatewayConfig.order && (
        <GoSellPayment gatewayConfig={gatewayConfig} />
      )}
      <VStack
        padding="80px 0"
        display="flex"
        flexDir="column"
        alignItems="center"
      >
        <HStack
          display="flex"
          flexDir={{ base: "column", lg: "row" }}
          alignItems={{ base: "center" }}
        >
          {quotations.map((quotation) => (
            <SubscriptionCard
              key={quotation.id}
              quotation={quotation}
              selectMethod={selectMethod}
              setHeighPrices={setHeighPrices}
              setFileName={setFileName}
              setSubscriptionData={setSubscriptionData}
              subscribeHeighPrice={subscribeHeighPrice}
              setChangeQuotationSectionVisibility={
                setChangeQuotationSectionVisibility
              }
              handleOpening={handleOpening}
              onFreeTrialSubscribe={handleFreeTrialOpening}
              processing={processing}
              isFreeTrial={quotation.price === 0}
              selectPaymentMethods={quotation.price === 0 ? ["card"] : []}
            />
          ))}
          {!user.subscriptions.some((sub) => sub.status === "active" && new Date(sub.endDate) > new Date()) && (
            <SubscriptionCard
              quotation={{
                duration: 7,
                price: 0,
              }}
              selectMethod={selectMethod}
              setHeighPrices={setHeighPrices}
              setFileName={setFileName}
              setSubscriptionData={setSubscriptionData}
              subscribeHeighPrice={subscribeHeighPrice}
              setChangeQuotationSectionVisibility={
                setChangeQuotationSectionVisibility
              }
              handleOpening={handleOpening}
              onFreeTrialSubscribe={handleFreeTrialOpening}
              processing={processing}
              isFreeTrial={true}
              selectPaymentMethods={[]}
            />
          )}
        </HStack>
        <Modal {...disclosureProps} onClose={disclosureProps.onClose}>
          <ModalOverlay />
          <ModalContent
            width={{ base: "300px", sm: "400px", md: "500px" }}
            minWidth={{ lg: "600px" }}
            marginTop="150px"
          >
            <ModalHeader>
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody>
              <FormControl as="form">
                {message.content && (
                  <Box margin="30px 0">
                    <Text color={message.color}>
                      <FormattedMessage id={message.content || ""} />
                    </Text>
                  </Box>
                )}
                {processing && (
                  <Box margin="30px 0">
                    <Text color="grey">
                      <FormattedMessage id="wait" />
                    </Text>
                  </Box>
                )}
                {fileName === "proveUrl" &&
                subscriptionData?.paymentMethod !== "bank" ? null : (
                  <>
                    <Text color="grey" fontSize={18} fontWeight="bold">
                      <FormattedMessage id={fileName || ""} />
                    </Text>
                    <Box margin="30px 0">
                      <Input
                        type="file"
                        required
                        name={fileName}
                        onChange={handleMedia}
                        key={fileName}
                      />
                      {progress > 0 && <Progress value={progress} />}
                    </Box>
                  </>
                )}
                {fileName === "proveUrl" && (
                  <Box textAlign="center" margin="30px 0">
                    <Button
                      backgroundColor="brand.900"
                      color="white"
                      height="50px"
                      _hover={{ filter: "brightness(120%)" }}
                      onClick={subscribe}
                      disabled={
                        (!subscriptionData?.proveUrl &&
                          subscriptionData?.paymentMethod === "bank") ||
                        processing ||
                        (!subscriptionData.nationalityProofUrl && !heighPrices)
                      }
                    >
                      <FormattedMessage id="subscribe" />
                    </Button>
                  </Box>
                )}
              </FormControl>
            </ModalBody>
          </ModalContent>
        </Modal>
        
        {/* Gateway Selection Modal */}
        <Modal {...gatewayModalProps} onClose={handleGatewayModalClose}>
          <ModalOverlay />
          <ModalContent
            width={{ base: "300px", sm: "400px", md: "500px" }}
            minWidth={{ lg: "500px" }}
            marginTop="150px"
          >
            <ModalHeader>
              <FormattedMessage id="payment.gateway.select" defaultMessage="Select Payment Gateway" />
              <ModalCloseButton />
            </ModalHeader>
            <ModalBody px={6}>
              <VStack spacing={4} padding="20px 0">
                <Text fontSize="16px" color="gray.600" textAlign="center">
                  <FormattedMessage 
                    id="payment.gateway.select.description" 
                    defaultMessage="Choose your preferred payment provider" 
                  />
                </Text>
                <HStack
                  spacing={4}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Button
                    px={8}
                    backgroundColor="brand.900"
                    color="white"
                    height="60px"
                    _hover={{ filter: "brightness(120%)" }}
                    onClick={() => handleGatewaySelection('tap')}
                  >
                    <FormattedMessage id="gateway.tap" defaultMessage="Tap" />
                  </Button>
                  <Button
                    px={8}
                    backgroundColor="#1a73e8"
                    color="white"
                    height="60px"
                    _hover={{ filter: "brightness(120%)" }}
                    onClick={() => handleGatewaySelection('myfatoorah')}
                  >
                    <FormattedMessage id="gateway.myfatoorah" defaultMessage="MyFatoorah" />
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </>
  );
};

export default Subscription;
